// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsMissionGetAllAttrs,
  IAboutUsMissionGetAttrs,
  IAboutUsMissionSaveAttrs,
} from 'interfaces/back-office/content-management/about-us/mission/about-us-mission-view.interface'
import {
  IAboutUsMissionApiGetAllResponse,
  IAboutUsMissionApiGetResponse,
  IAboutUsMissionApiPaginatedResponse,
  IAboutUsMissionApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/mission/about-us-mission-api.interface'
import { IAboutUsMissionDetail } from 'interfaces/back-office/content-management/about-us/mission'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// About Us Mission Props
const aboutUsMissionProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  content: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// About Us Mission List Model
const AboutUsMissions = types
  .model('AboutUsMissions')
  .props(withHelper_baseResponsePaginationTypes(types.model('AboutUsMissionList').props(aboutUsMissionProps)))

// About Us Mission Detail Model
const AboutUsMissionDetail = types.model('AboutUsMissionDetail').props(aboutUsMissionProps)

const AboutUsMissionStoreModel = types
  .model('AboutUsMission')
  .props({
    aboutUsMission_isLoading: types.optional(types.boolean, false),
    aboutUsMission_isSaveLoading: types.optional(types.boolean, false),
    aboutUsMission_aboutUsMissions: types.optional(AboutUsMissions, {}),
    aboutUsMission_detail: types.optional(AboutUsMissionDetail, {}),
  })
  .actions(self => ({
    aboutUsMission_SET_LOADING: (payload: boolean): void => {
      self.aboutUsMission_isLoading = payload
    },
    aboutUsMission_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsMission_isSaveLoading = payload
    },
    aboutUsMission_SET_ABOUT_US_MISSIONS: (payload: IAboutUsMissionApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.aboutUsMission_aboutUsMissions = cast(mapPayload)
    },
    aboutUsMission_SET_ABOUT_US_MISSION: (payload: IAboutUsMissionDetail): void => {
      self.aboutUsMission_detail = cast(payload)
    },
    aboutUsMission_CLEAR_ABOUT_US_MISSIONS: (): void => {
      self.aboutUsMission_aboutUsMissions = cast({})
    },
    aboutUsMission_CLEAR_ABOUT_US_MISSION: (): void => {
      self.aboutUsMission_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about Us Missions
     *
     * @param {IAboutUsMissionGetAllAttrs} payload
     *
     * @return {any} any
     */
    aboutUsMission_fetchAboutUsMissions: flow(function* (payload: IAboutUsMissionGetAllAttrs) {
      self.aboutUsMission_SET_LOADING(true)

      const response: IAboutUsMissionApiGetAllResponse =
        yield self.environment.aboutUsMissionApi.aboutUsMission_getAboutUsMissions({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) self.aboutUsMission_SET_ABOUT_US_MISSIONS(response.res.data)

      self.aboutUsMission_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us mission
     *
     * @param {IAboutUsMissionGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsMission_fetchAboutUsMission: flow(function* (payload: IAboutUsMissionGetAttrs) {
      self.aboutUsMission_SET_LOADING(true)

      const response: IAboutUsMissionApiGetResponse = yield self.environment.aboutUsMissionApi.aboutUsMission_getAboutUsMission({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.aboutUsMission_SET_ABOUT_US_MISSION(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsMission_SET_LOADING(false)
    }),

    /**
     * @description Save about us mission
     *
     * @param {IAboutUsMissionSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsMission_saveAboutUsMission: flow(function* (payload: IAboutUsMissionSaveAttrs) {
      self.aboutUsMission_SET_SAVE_LOADING(true)

      const response: IAboutUsMissionApiSaveResponse = yield self.environment.aboutUsMissionApi.aboutUsMission_saveAboutUsMission(
        {
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        },
      )
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsMission_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsMissionStoreDefaultModel = () => types.optional(AboutUsMissionStoreModel, {})
